import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Radio } from '@components/core-ui/form-elements/Radio';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { Tooltip } from '@components/core-ui/Tooltip';
import { useAPI } from '@providers/useAPI';
import { AdminService, AdminWorkspaceListItemResponseDto, JobsAdminService } from 'app/API';
import { useEffect, useState } from 'react';

import { Content } from './MoveJobToOrgWsModal/styles/Content';

type TProps = {
  jobId: string;
  workspaceId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AdminMoveJobToOrgWsModal = ({ jobId, workspaceId, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(workspaceId);
  const [allOrganizations, setAllOrganizations] = useState<{ value: string | number; label: string }[]>([]);
  const [allWorkspaces, setAllWorkspaces] = useState<AdminWorkspaceListItemResponseDto[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const workspaces = allWorkspaces.filter((ws) => ws.organizationId === selectedOrganizationId);

  useEffect(() => {
    call(AdminService.getAllOrganizationsList()).then((orgs) => {
      setAllOrganizations(orgs.map((org) => ({ value: org.id, label: `${org.name} (${org.slug})` })));
    });
    call(AdminService.getAllWorkspacesList()).then((orgs) => {
      setAllWorkspaces(orgs);
    });
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await call(
      JobsAdminService.adminMoveJobToOrgWs({ jobId, requestBody: { workspaceId: selectedWorkspaceId ?? '' } }),
    );
    setIsSubmitting(false);
    onSuccess();
    onCancel();
  };

  return (
    <ModalWrapper width="487px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Move Job to another workspace</ModalTitle>
      <Content>
        <AutoCompleteSelect
          name="organizationId"
          label="Organization"
          className="form-autocomplete"
          value={selectedOrganizationId}
          onChange={(_, value) => setSelectedOrganizationId(value)}
          options={allOrganizations}
          required={false}
        />

        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Workspace Name</Th>
              </Tr>
            </Thead>
            <Tbody>
              {workspaces.map((data) => {
                const disabled = workspaceId === data.id;
                return (
                  <Tr key={data.id}>
                    <Td>
                      {disabled ? (
                        <Tooltip content="Cannot move the job to the same Workspace" placement="bottom">
                          <div className="radioWrapper disabled">
                            <Radio onChange={() => {}} checked={selectedWorkspaceId === data.id} disabled={true}>
                              {data.name} ({data.slug})
                            </Radio>
                          </div>
                        </Tooltip>
                      ) : (
                        <div className="radioWrapper">
                          <Radio
                            onChange={() => setSelectedWorkspaceId(data.id)}
                            checked={selectedWorkspaceId === data.id}
                          >
                            {data.name} ({data.slug})
                          </Radio>
                        </div>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={selectedWorkspaceId === workspaceId} onClick={handleSubmit}>
            Move
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
